import { ApiError, apiFetch } from './http.js';

/**
 * Cliente da Equipe de especialistas. Mantém os mesmos nomes e formatos das
 * telas aprovadas (lista, tela de cada um, configuração e leitura).
 */

/** Texto do erro para mostrar à pessoa: a mensagem do servidor, ou um aviso geral. */
export function describePAApiError(
  err: unknown,
  fallback = 'Não consegui fazer isso agora. Tenta de novo?',
): string {
  // Compara pelo nome: em desenvolvimento o módulo pode ser recarregado e a classe duplicada.
  const named = err as { name?: string | undefined; message?: string } | null;
  if ((err instanceof ApiError || named?.name === 'ApiError') && named?.message) {
    return named.message;
  }
  return fallback;
}

export type SpecialistTheme =
  | 'sparkles'
  | 'sun'
  | 'newspaper'
  | 'invoice'
  | 'chart'
  | 'cloud'
  | 'gift'
  | 'chat-bubble'
  | 'price-tag'
  | 'cart'
  | 'plane';

export interface PASpecialistSummary {
  key: string;
  name: string;
  shortDescription: string;
  description: string;
  category: string;
  lifecycle: 'available' | 'coming_soon' | 'planned';
  enabled: boolean;
  theme: SpecialistTheme;
  accentColor: string;
  unreadCount: number;
  built: boolean;
  /** Benefícios e exemplos já retornados pelo catálogo para a página da especialidade. */
  ownerCommandExamples: string[];
  capabilities: string[];
  friendlyRules: string[];
}

export function getSpecialists() {
  return apiFetch<{ specialists: PASpecialistSummary[] }>('/v1/specialists');
}

export interface PASpecialistFeedItem {
  id: string;
  kind: string;
  title: string;
  summary: string | null;
  imageUrl: string | null;
  url: string | null;
  /** Assunto (ex.: 'carros') e fonte (ex.: 'g1') — hoje só no especialista de Notícias. */
  theme: string | null;
  source: string | null;
  details?: Record<string, unknown> | undefined;
  createdAt: string;
  readAt: string | null;
}

export interface PASpecialistFeed {
  skill: Pick<
    PASpecialistSummary,
    | 'key'
    | 'name'
    | 'description'
    | 'shortDescription'
    | 'lifecycle'
    | 'theme'
    | 'accentColor'
    | 'ownerCommandExamples'
    | 'capabilities'
    | 'friendlyRules'
  >;
  config: Record<string, unknown>;
  /** O servidor está separando as de hoje agora — a tela se atualiza sozinha. */
  generating?: boolean | undefined;
  items: PASpecialistFeedItem[];
}

export function getSpecialistFeed(id: string) {
  return apiFetch<PASpecialistFeed>(`/v1/specialists/${id}/feed`);
}

export function updateSpecialistConfig(id: string, config: Record<string, unknown>) {
  return apiFetch<{ ok: true; config: Record<string, unknown>; generated: boolean }>(
    `/v1/specialists/${id}/config`,
    { method: 'PUT', body: config },
  );
}

/** Salva os assuntos de Notícias — o servidor já separa as notícias de hoje na hora. */
export function saveNewsTopics(themes: string[]) {
  return updateSpecialistConfig('noticias', { themes });
}

export interface HoroscopeCity {
  id: string;
  city: string;
  region: string;
  country: string;
  countryCode: string;
  label: string;
  latitude: number;
  longitude: number;
  timezone: string;
}

export type HoroscopeBirthTimeAccuracy = 'exact' | 'approximate' | 'unknown';

export interface HoroscopeProfileInput {
  birthDate: string;
  birthTime: string | null;
  birthTimeAccuracy: HoroscopeBirthTimeAccuracy;
  place: HoroscopeCity;
}

export function searchHoroscopeCities(query: string) {
  return apiFetch<{ cities: HoroscopeCity[] }>(
    `/v1/specialists/horoscopo/cities?q=${encodeURIComponent(query)}`,
  );
}

export function saveHoroscopeProfile(
  profile: HoroscopeProfileInput,
  notifications: { morningBrief: boolean; transitAlerts: boolean },
) {
  return updateSpecialistConfig('horoscopo', { profile, notifications });
}

export interface WeatherCity {
  id: string;
  city: string;
  state: string;
  stateName: string;
  label: string;
}

export type WeatherTransport = 'car' | 'motorcycle' | 'transit' | 'walk' | 'bike';

export function searchWeatherCities(query: string) {
  return apiFetch<{ cities: WeatherCity[] }>(
    `/v1/specialists/clima/cities?q=${encodeURIComponent(query)}`,
  );
}

export function saveWeatherProfile(
  place: WeatherCity,
  routine: { transport: WeatherTransport; sensitivities: string[] },
  notifications: { morningBrief: boolean; severeAlerts: boolean; beforeAppointments: boolean },
) {
  return updateSpecialistConfig('clima', { place, routine, notifications });
}

export type PersonalBillRecurrence = 'none' | 'monthly';
export type PersonalBillStatus = 'open' | 'paid';

export interface PersonalBill {
  id: string;
  title: string;
  amountCents: number;
  dueDate: string;
  recurrence: PersonalBillRecurrence;
  status: PersonalBillStatus;
  createdAt: string;
  paidAt?: string | undefined;
}

export interface PersonalBillsSummary {
  openCount: number;
  overdueCount: number;
  dueThisWeekCount: number;
  openTotalCents: number;
  dueThisWeekTotalCents: number;
  nextDueDate: string | null;
}

export function startBillsSpecialist() {
  return updateSpecialistConfig('contas', { preferences: { weekStartsOn: 'monday' } });
}

export type PersonalBillAction =
  | {
      type: 'add';
      title: string;
      amountCents: number;
      dueDate: string;
      recurrence: PersonalBillRecurrence;
    }
  | {
      type: 'update';
      id: string;
      title?: string | undefined;
      amountCents?: number | undefined;
      dueDate?: string | undefined;
      recurrence?: PersonalBillRecurrence | undefined;
    }
  | { type: 'mark_paid' | 'reopen' | 'delete'; id: string };

export function changePersonalBill(action: PersonalBillAction) {
  return updateSpecialistConfig('contas', { billAction: action });
}

/** Ganhos (Dinheiro): ficam na mesma habilidade das contas, cifrados no servidor. */
export const INCOME_CATEGORIES = [
  'salario',
  'freela',
  'renda_extra',
  'rendimentos',
  'aluguel',
  'outros',
] as const;
export type IncomeCategory = (typeof INCOME_CATEGORIES)[number];
export const INCOME_CATEGORY_LABEL: Record<IncomeCategory, string> = {
  salario: 'Salário',
  freela: 'Freela',
  renda_extra: 'Renda extra',
  rendimentos: 'Rendimentos',
  aluguel: 'Aluguel recebido',
  outros: 'Outros',
};

export interface PersonalIncome {
  id: string;
  title: string;
  amountCents: number;
  date: string;
  recurrence: PersonalBillRecurrence;
  status: 'expected' | 'received' | 'skipped';
  category: IncomeCategory;
  label?: string | undefined;
  createdAt: string;
  receivedAt?: string | undefined;
}

export interface MoneyMonth {
  month: string;
  receivedCents: number;
  expectedCents: number;
  paidCents: number;
  dueCents: number;
  leftoverCents: number;
  savingsRate: number | null;
  byCategory: { category: IncomeCategory; label: string; cents: number }[];
  byLabel: { label: string; cents: number }[];
}

export type PersonalIncomeAction =
  | {
      type: 'add';
      title: string;
      amountCents: number;
      date: string;
      recurrence: PersonalBillRecurrence;
      received: boolean;
      category: IncomeCategory;
      label?: string | undefined;
    }
  | {
      type: 'update';
      id: string;
      title?: string | undefined;
      amountCents?: number | undefined;
      date?: string | undefined;
      recurrence?: PersonalBillRecurrence | undefined;
      category?: IncomeCategory | undefined;
      /** Vazio tira o rótulo. */
      label?: string | undefined;
    }
  | { type: 'receive'; id: string; amountCents?: number | undefined }
  | { type: 'skip' | 'reopen' | 'delete'; id: string };

export function changePersonalIncome(action: PersonalIncomeAction) {
  return updateSpecialistConfig('contas', { incomeAction: action });
}

/** O que o servidor manda junto das contas (servidores antigos não mandam: fica vazio). */
export function readMoney(config: Record<string, unknown> | undefined) {
  const incomes = Array.isArray(config?.incomes) ? (config.incomes as PersonalIncome[]) : [];
  const labels = Array.isArray(config?.incomeLabels) ? (config.incomeLabels as string[]) : [];
  const month =
    config?.money && typeof config.money === 'object' ? (config.money as MoneyMonth) : null;
  const history = Array.isArray(config?.moneyHistory) ? (config.moneyHistory as MoneyMonth[]) : [];
  return { incomes, labels, month, history };
}

export type ShoppingCategory =
  | 'hortifruti'
  | 'padaria'
  | 'geladeira'
  | 'despensa'
  | 'bebidas'
  | 'limpeza'
  | 'cuidados'
  | 'outros';
export interface PersonalShoppingItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  category: ShoppingCategory;
  estimatedPriceCents?: number | undefined;
  note?: string | undefined;
  status: 'needed' | 'picked';
  createdAt: string;
  pickedAt?: string | undefined;
}
export type ShoppingItemAction =
  | {
      type: 'add';
      name: string;
      quantity: number;
      unit: string;
      category?: ShoppingCategory | undefined;
      estimatedPriceCents?: number | undefined;
      note?: string | undefined;
    }
  | {
      type: 'update';
      id: string;
      name?: string | undefined;
      quantity?: number | undefined;
      unit?: string | undefined;
      category?: ShoppingCategory | undefined;
      estimatedPriceCents?: number | undefined;
      note?: string | undefined;
    }
  | { type: 'toggle' | 'delete'; id: string }
  | { type: 'clear_picked' | 'reset' };
export const startShoppingList = () => updateSpecialistConfig('lista_compras', { start: true });
export const changeShoppingItem = (action: ShoppingItemAction) =>
  updateSpecialistConfig('lista_compras', { itemAction: action });

export type ImportantDateKind = 'birthday' | 'anniversary' | 'personal';
export type ImportantDateRecurrence = 'annual' | 'once';
export interface PersonalImportantDate {
  id: string;
  title: string;
  date: string;
  kind: ImportantDateKind;
  recurrence: ImportantDateRecurrence;
  personName?: string | undefined;
  note?: string | undefined;
  createdAt: string;
}
export interface UpcomingImportantDate extends PersonalImportantDate {
  source: 'personal' | 'contact';
  nextDate: string;
  daysUntil: number;
}
export type ImportantDateAction =
  | {
      type: 'add';
      title: string;
      date: string;
      kind: ImportantDateKind;
      recurrence: ImportantDateRecurrence;
      personName?: string | undefined;
      note?: string | undefined;
    }
  | {
      type: 'update';
      id: string;
      title?: string | undefined;
      date?: string | undefined;
      kind?: ImportantDateKind | undefined;
      recurrence?: ImportantDateRecurrence | undefined;
      personName?: string | undefined;
      note?: string | undefined;
    }
  | { type: 'delete'; id: string };
export const startImportantDates = () =>
  updateSpecialistConfig('datas_importantes', { start: true });
export const changeImportantDate = (action: ImportantDateAction) =>
  updateSpecialistConfig('datas_importantes', { dateAction: action });

export interface PriceObservation {
  priceCents: number;
  observedAt: string;
  source: 'manual' | 'mercado_livre';
}
export interface PriceWatch {
  id: string;
  title: string;
  targetPriceCents: number;
  currentPriceCents: number;
  currency: 'BRL';
  url?: string | undefined;
  provider: 'manual' | 'mercado_livre';
  providerItemId?: string | undefined;
  imageUrl?: string | undefined;
  history: PriceObservation[];
  createdAt: string;
  updatedAt: string;
  metrics: { targetReached: boolean; bestPriceCents: number; changePercent: number };
}
export type PriceWatchAction =
  | {
      type: 'add';
      title: string;
      targetPriceCents: number;
      currentPriceCents: number;
      url?: string | undefined;
    }
  | { type: 'observe'; id: string; priceCents: number }
  | { type: 'refresh' | 'delete'; id: string }
  | { type: 'update_target'; id: string; targetPriceCents: number };
export const startPriceRadar = () => updateSpecialistConfig('radar_precos', { start: true });
export const changePriceWatch = (action: PriceWatchAction) =>
  updateSpecialistConfig('radar_precos', { watchAction: action });

export type MorningSection =
  'agenda' | 'weather' | 'bills' | 'dates' | 'shopping' | 'news' | 'horoscope' | 'waiting';
export const saveMorningBrief = (input: {
  greetingName?: string | undefined;
  preferredTime?: string | undefined;
  sections: MorningSection[];
}) => updateSpecialistConfig('bom_dia', input);

export function markSpecialistRead(id: string) {
  return apiFetch<{ ok: true }>(`/v1/specialists/${id}/read`, { method: 'POST' });
}
